export const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://core.manrina.fr';

/**
 * GET — cookie httpOnly envoye automatiquement via credentials: 'include'
 * Le param token est garde pour retrocompat des signatures mais ignore
 */
async function apiFetch(endpoint, _token = null) {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`Erreur ${response.status}`);
  }
  const json = await response.json();
  return json.data !== undefined ? json.data : json;
}

// ============================================================================
// HELPERS DATES — fuseau Martinique
// ============================================================================
// Les dates des events sont stockees en UTC en DB. Sans timezone explicite,
// `new Date(marche.date) >= new Date()` peut donner un faux resultat le jour J
// (event "samedi" stocke `2026-05-03T00:00:00Z` se lit `2026-05-02 20:00` en
// heure Martinique → marque TERMINE le matin du marche).

const MQ_TIMEZONE = 'America/Martinique'

/** YYYY-MM-DD en heure Martinique (string lexicographiquement comparable).
 * Cas special : T00:00:00Z (PostgreSQL @db.Date) → composants UTC directement
 * (sinon decalerait d'un jour en arriere a Martinique UTC-4). */
export function toMartiniqueDay(date) {
  if (!date) return null
  const d = date instanceof Date ? date : new Date(date)
  if (isNaN(d.getTime())) return null
  if (d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0) {
    const year = d.getUTCFullYear()
    const month = String(d.getUTCMonth() + 1).padStart(2, '0')
    const day = String(d.getUTCDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }
  return d.toLocaleDateString('en-CA', { timeZone: MQ_TIMEZONE })
}

/** Compare 2 dates au jour (Martinique). -1 / 0 / 1. */
export function compareDays(a, b) {
  const da = toMartiniqueDay(a)
  const db = toMartiniqueDay(b)
  if (da === null || db === null) return 0
  if (da > db) return 1
  if (da < db) return -1
  return 0
}

/** Vrai si date >= aujourd'hui (Martinique) — utilisable pour filtrer "futurs + aujourd'hui". */
export function isFutureOrToday(date) {
  return compareDays(date, new Date()) >= 0
}

/** Vrai si date strictement < aujourd'hui (Martinique) — pour filtrer "passes". */
export function isPast(date) {
  return compareDays(date, new Date()) < 0
}

export function fixImageUrl(url) {
  if (!url) return null;
  if (url.startsWith('http://localhost')) {
    return url.replace(/http:\/\/localhost:\d+/, API_BASE_URL);
  }
  if (url.startsWith('/uploads')) {
    return `${API_BASE_URL}${url}`;
  }
  return url;
}

/**
 * ⚠️ JOUR CALENDAIRE vs INSTANT — la regle qui evite le decalage d'un jour.
 *
 * Une colonne Prisma `@db.Date` (`Event.date`, `Tour.dateCollecte`,
 * `ModuleInvoice.dateEmission`...) ne porte PAS un instant : elle porte un JOUR.
 * L'API la serialise en `2026-09-05T00:00:00.000Z`. Rendue avec le fuseau du
 * navigateur — la Martinique est a UTC-4 — cette date devient le 4 septembre a
 * 20 h, donc « vendredi 4 » au lieu de « samedi 5 ». Le bug est invisible au
 * developpeur (Europe/Paris est en avance : le jour ne change pas) et systematique
 * chez l'utilisateur (remonte par Enzo, captures du 1er septembre : la grande
 * carte disait vendredi 4, la petite samedi 5, pour le MEME evenement).
 *
 * D'ou deux familles de formateurs, a ne jamais melanger :
 *  - **jour calendaire** (`@db.Date`) → `formatDate` / `formatDateCourte`, qui
 *    lisent en UTC. C'est le cas de toutes les dates d'evenement.
 *  - **instant** (`DateTime` vrai : `createdAt`, `inscriptionDe`, `repartitionAt`)
 *    → `toLocaleDateString` local, qui est alors le bon choix.
 *
 * En cas de doute : ouvrir `schema.prisma` et regarder si le champ porte `@db.Date`.
 */

/**
 * Date longue « Samedi 15 août » — l'ANNEE n'apparait que si ce n'est pas
 * l'annee en cours (audit design M2).
 *
 * Le prototype ne l'affiche jamais, et la chaine plus longue faisait passer la
 * puce horaire a la ligne sur les cartes d'evenement : l'alignement cassait des
 * la deuxieme carte. On la garde pour les autres annees, ou elle informe
 * vraiment (« Samedi 15 août 2027 »).
 *
 * L'annee de reference est celle de la Martinique, pas celle du navigateur :
 * le 31 decembre a 21 h locale, l'UTC est deja au 1er janvier suivant.
 */
export function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const jours = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
  const mois = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin',
    'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
  const anneeCourante = Number((toMartiniqueDay(new Date()) || '').slice(0, 4));
  const annee = d.getUTCFullYear();
  const suffixeAnnee = annee === anneeCourante ? '' : ` ${annee}`;
  return `${jours[d.getUTCDay()]} ${d.getUTCDate()} ${mois[d.getUTCMonth()]}${suffixeAnnee}`;
}

/**
 * Date courte « 5 sept. » pour un JOUR CALENDAIRE (`@db.Date`) — meme lecture
 * UTC que `formatDate`, cf. la regle ci-dessus.
 */
export function formatDateCourte(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  const mois = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin',
    'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
  return `${d.getUTCDate()} ${mois[d.getUTCMonth()]}`;
}

/** Jour calendaire numerique « 05/09 » (`@db.Date`). */
export function formatDateNumerique(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  const jj = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${jj}/${mm}`;
}

async function apiPost(endpoint, data, _token = null) {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  });
  const json = await response.json();
  if (!response.ok) {
    throw new Error(json.message || json.data?.message || `Erreur ${response.status}`);
  }
  return json.data !== undefined ? json.data : json;
}

async function apiDelete(endpoint, _token) {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  const json = await response.json();
  if (!response.ok) {
    throw new Error(json.message || json.data?.message || `Erreur ${response.status}`);
  }
  return json.data !== undefined ? json.data : json;
}

async function apiPatch(endpoint, data, _token) {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  });
  const json = await response.json();
  if (!response.ok) {
    throw new Error(json.message || json.data?.message || `Erreur ${response.status}`);
  }
  return json.data !== undefined ? json.data : json;
}

// Adaptateur retrocompat: le backend retourne maintenant "event" au lieu de "marche"
// dans les relations participation. On normalise pour garder ".marche" cote frontend.
function normalizeParticipations(data) {
  if (!data) return data;
  const arr = Array.isArray(data) ? data : [data];
  arr.forEach(item => {
    // Participation directe (p.event → p.marche)
    if (item.event && !item.marche) {
      item.marche = item.event;
    }
    // Participation eventId → marcheId (pour compatibilite)
    if (item.eventId && !item.marcheId) {
      item.marcheId = item.eventId;
    }
    // Invoice avec participation (invoice.participation.event → .marche)
    if (item.participation?.event && !item.participation?.marche) {
      item.participation.marche = item.participation.event;
    }
    if (item.participation?.eventId && !item.participation?.marcheId) {
      item.participation.marcheId = item.participation.eventId;
    }
  });
  return data;
}

export const api = {
  getMarches: () => apiFetch('/public/marches'),
  getMarcheDetail: (id) => apiFetch(`/public/marches/${id}`),
  getProducteur: (id) => apiFetch(`/public/producteurs/${id}`),
  getPortalSettings: () => apiFetch('/public/portal-settings'),
  // Lieux de l'Explorer + etat (Bientot/Actif) pilote au BO
  getModules: () => apiFetch('/public/modules'),
  // Paniers (tournees) — lecture (inc.1).
  getTournees: () => apiFetch('/public/tournees'),
  getTourneeDetail: (id) => apiFetch(`/public/tournees/${id}`),
  // Commande client (inc.3-4, JWT portail) — POST cree commande + (si carte) PaymentIntent
  // (reponse : { order, paiement: { requiresPayment, clientSecret, publishableKey, montantAvoir, montantCarte } }).
  createClientOrder: (dto) => apiPost('/client/orders', dto),
  getClientOrders: () => apiFetch('/client/orders'),
  getClientOrder: (id) => apiFetch(`/client/orders/${id}`),
  // Points de retrait actifs (checkout) + solde wallet (avoirs client, ADR-050)
  getPickupPoints: () => apiFetch('/public/pickup-points'),
  getWallet: () => apiFetch('/wallet/me'),
  // Recharge wallet (ADR-058 §7) : cree le PaymentIntent de depot
  // (reponse : { clientSecret, publishableKey, paymentIntentId, montant })
  createWalletRecharge: (montant) => apiPost('/wallet/recharge', { montant }),
  // Verification active apres paiement : le backend re-lit le PaymentIntent
  // chez Stripe et credite si paye (filet de securite du webhook, idempotent)
  verifyWalletRecharge: (paymentIntentId) => apiPost('/wallet/recharge/verify', { paymentIntentId }),

  // Auth API
  auth: {
    login: (email, password) => apiPost('/auth/login', { email, password }),
    requestOtp: (email, nom) => apiPost('/auth/request-otp', { email, nom }),
    verifyOtp: (email, code, nom) => apiPost('/auth/verify-otp', { email, code, ...(nom ? { nom } : {}) }),
    oauth: (provider, idToken, nonce) => apiPost('/auth/oauth', { provider, idToken, nonce }),
    getMe: () => apiFetch('/auth/me'),
    updateProfile: (data) => apiPatch('/auth/me', data),
    logout: () => apiPost('/auth/logout', {}),
    // Auth PIN (telephone + code PIN)
    loginPin: (telephone, pin) => apiPost('/auth/login-pin', { telephone, pin }),
    registerPin: (telephone, pin, prenom, nom) => apiPost('/auth/register-pin', { telephone, pin, prenom, nom }),
    // Liaison de comptes (ADR-027 etape 4)
    getAuthMethods: () => apiFetch('/auth/me/methods'),
    linkEmail: (email) => apiPost('/auth/link-email', { email }),
    verifyLinkEmail: (email, code) => apiPost('/auth/verify-link-email', { email, code }),
    linkPhonePin: (telephone, pin) => apiPost('/auth/link-phone-pin', { telephone, pin }),
    setPassword: (password) => apiPost('/auth/set-password', { password }),
  },

  // Espace Producteur
  producer: {
    getMe: () => apiFetch('/producer/me'),
    register: (data) => apiPost('/producer/register', data),
    claim: (code) => apiPost('/producer/claim', { code }),
    claimByOrgId: (organizationId) => apiPost('/producer/claim-by-id', { organizationId }),
    checkSimilar: (params) => {
      const qs = new URLSearchParams(
        Object.entries(params || {}).filter(([, v]) => v != null && v !== '')
      ).toString()
      return apiFetch(`/producer/check-similar${qs ? `?${qs}` : ''}`)
    },
    getMyClaim: () => apiFetch('/producer/my-claim'),
    updateMe: (data) => apiPatch('/producer/me', data),
    getOffers: () => apiFetch('/producer/offers'),
    getStats: () => apiFetch('/producer/stats'),
    getMarches: () => apiFetch('/producer/marches').then(normalizeParticipations),
    getAvailableMarches: () => apiFetch('/producer/available-marches').then(normalizeParticipations),
    joinMarche: (marcheId) => apiPost('/producer/join-marche', { marcheId }).then(d => normalizeParticipations([d])?.[0] || d),
    confirmParticipation: (id) => apiPatch(`/producer/confirm-participation/${id}`, {}).then(d => normalizeParticipations([d])?.[0] || d),
    getHistory: () => apiFetch('/producer/history').then(normalizeParticipations),
    getPendingCa: () => apiFetch('/producer/pending-ca').then(normalizeParticipations),
    submitCa: (participationId, ca) => apiPatch(`/producer/participations/${participationId}/ca`, { ca }).then(d => normalizeParticipations([d])?.[0] || d),
    updateOffer: (id, data) => apiPatch(`/producer/offers/${id}`, data),
    withdrawParticipation: (id) => apiDelete(`/producer/withdraw-participation/${id}`),
    // Catalogue et creation d'offres
    // Canaux de vente (toggles Tournee/Marche/Atelier — v7.1 sujet 4)
    setOfferChannel: (offerId, moduleCode, isActive, prixCanal) =>
      apiPatch(`/producer/offers/${offerId}/channels`, { moduleCode, isActive, ...(prixCanal != null ? { prixCanal } : {}) }),
    // Certifications (preuve -> verification -> badge, ADR-056)
    getCertifications: () => apiFetch('/producer/certifications'),
    submitCertifDocument: async (code, file, siteNom) => {
      const formData = new FormData();
      formData.append('file', file);
      if (siteNom) formData.append('siteNom', siteNom);
      const response = await fetch(`${API_BASE_URL}/producer/certifications/${code}/document`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      const json = await response.json();
      if (!response.ok) throw new Error(json.message || `Erreur ${response.status}`);
      return json.data !== undefined ? json.data : json;
    },
    acceptCertifEngagement: (code) =>
      apiPost(`/producer/certifications/${code}/engagement`, { accepte: true }),
    // Dossier exposant (ADR-071, v7.5) — l'API renvoie l'ETAT CALCULE complet
    // (pieces demandees, manquants, niveau) : le portail ne recalcule rien.
    getDossierExposant: () => apiFetch('/producer/dossier-exposant'),
    majDossierExposant: (data) => apiPatch('/producer/dossier-exposant', data),
    chercherEntreprise: (q) => apiFetch(`/producer/dossier-exposant/registre?q=${encodeURIComponent(q)}`),
    envoyerPieceDossier: async (code, file, dateValidite) => {
      const formData = new FormData();
      formData.append('file', file);
      if (dateValidite) formData.append('dateValidite', dateValidite);
      const response = await fetch(`${API_BASE_URL}/producer/dossier-exposant/pieces/${code}/document`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      let json = {};
      try { json = await response.json(); } catch { /* reponse vide (proxy) */ }
      if (!response.ok) {
        // Messages en clair (brief §5.2) : le poids d'abord, qu'un proxy peut couper.
        if (response.status === 413) throw new Error('Fichier trop lourd : 10 Mo maximum. Essayez une photo, ou un PDF plus léger.');
        const m = Array.isArray(json.message) ? json.message.join(' ') : json.message;
        throw new Error(m || `Envoi impossible (erreur ${response.status}). Réessayez.`);
      }
      return json.data !== undefined ? json.data : json;
    },
    getCatalog: (search, categoryId) => {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (categoryId) params.set('categoryId', categoryId);
      const qs = params.toString();
      return apiFetch(`/producer/catalog${qs ? `?${qs}` : ''}`);
    },
    getCatalogCategories: () => apiFetch('/producer/catalog/categories'),
    createOffer: (data) => apiPost('/producer/offers', data),
    deleteOffer: (id) => apiDelete(`/producer/offers/${id}`),
    // Conditionnements de l'offre (ADR-061) : « au kilo ET en barquettes de
    // 500 g ». Une seule ligne cote client, plusieurs formats derriere.
    getConditionnements: (offerId) => apiFetch(`/producer/offers/${offerId}/conditionnements`),
    addConditionnement: (offerId, data) =>
      apiPost(`/producer/offers/${offerId}/conditionnements`, data),
    updateConditionnement: (offerId, conditionnementId, data) =>
      apiPatch(`/producer/offers/${offerId}/conditionnements/${conditionnementId}`, data),
    removeConditionnement: (offerId, conditionnementId) =>
      apiDelete(`/producer/offers/${offerId}/conditionnements/${conditionnementId}`),
    // Proposition de produit
    proposeProduct: async (data, photo) => {
      const formData = new FormData();
      formData.append('nom', data.nom);
      formData.append('categoryId', data.categoryId);
      if (data.description) formData.append('description', data.description);
      if (data.unit) formData.append('unit', data.unit);
      if (data.requestExclusive) formData.append('requestExclusive', data.requestExclusive);
      if (photo) formData.append('photo', photo);
      const response = await fetch(`${API_BASE_URL}/producer/propose-product`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      const json = await response.json();
      if (!response.ok) throw new Error(json.message || `Erreur ${response.status}`);
      return json.data !== undefined ? json.data : json;
    },
    getMyProposals: () => apiFetch('/producer/my-proposals'),
    // Offres par stand
    getStandOffers: (participationId) => apiFetch(`/producer/participations/${participationId}/stand-offers`),
    addStandOffer: (participationId, productOfferId, data) => apiPost(`/producer/participations/${participationId}/stand-offers`, { productOfferId, ...data }),
    removeStandOffer: (participationId, productOfferId) => apiDelete(`/producer/participations/${participationId}/stand-offers/${productOfferId}`),
    setStandOffers: (participationId, productOfferIds) => apiPost(`/producer/participations/${participationId}/stand-offers/bulk`, { productOfferIds }),
    // Confirmation datee du stand (v7.1 sujet 3) : composition du jour + date,
    // publie le stand cote client (gate standConfirmeAt)
    confirmStand: (participationId, productOfferIds) => apiPost(`/producer/participations/${participationId}/confirm-stand`, { productOfferIds }),
    // Stand de reference (vitrine par defaut pre-cochee a chaque inscription)
    setStandDefaut: (productOfferId, standDefaut) => apiPatch(`/producer/offers/${productOfferId}/stand-defaut`, { standDefaut }),
    // Sequestre (ADR-058 §3) : preference de versement des ventes Paniers reparties
    setVersementMode: (mode) => apiPatch('/producer/versement-mode', { mode }),
    // Parcours producteur d'une tournee (v7.3, palier 1 — lecture seule)
    getTours: () => apiFetch('/producer/tours'),
    getTourPreparation: (tourId) => apiFetch(`/producer/tours/${tourId}/preparation`),
    getTourArgent: (tourId) => apiFetch(`/producer/tours/${tourId}/argent`),
    // Saisie des dispos + offres Paniers (v7.3 palier 2 — plafond stock regle B)
    getTourDispos: (tourId) => apiFetch(`/producer/tours/${tourId}/dispos`),
    addTourDispo: (tourId, productOfferId, quantite) => apiPost(`/producer/tours/${tourId}/dispos`, { productOfferId, quantite }),
    setTourDispo: (tourId, tourOfferId, quantite) => apiPatch(`/producer/tours/${tourId}/dispos/${tourOfferId}`, { quantite }),
    removeTourDispo: (tourId, tourOfferId) => apiDelete(`/producer/tours/${tourId}/dispos/${tourOfferId}`),
    // Ajustements (v7.3 palier 3, ADR-059) : impact avant demande, puis demande
    getTourImpact: (tourId, tourOfferId, quantiteManquante) =>
      apiFetch(`/producer/tours/${tourId}/dispos/${tourOfferId}/impact?quantiteManquante=${quantiteManquante}`),
    demanderAjustement: (tourId, tourOfferId, quantiteManquante, motif) =>
      apiPost(`/producer/tours/${tourId}/ajustements`, { tourOfferId, quantiteManquante, motif }),
    getPaniersOffres: () => apiFetch('/producer/tours/offres/paniers'),
    // Reglement de la commission marche (ADR-058 §5) — jamais le CA :
    // WALLET = compense sur le solde (regle) ; VIREMENT/ESPECES = declare (attente Manrina)
    payCommission: (participationId, mode) => apiPost(`/producer/participations/${participationId}/pay-commission`, { mode }),
    // CARTE : PaymentIntent plateforme ({ clientSecret, publishableKey, paymentIntentId, montant TTC })
    payCommissionCard: (participationId) => apiPost(`/producer/participations/${participationId}/pay-commission/card`, {}),
    verifyCommissionPayment: (participationId, paymentIntentId) => apiPost(`/producer/participations/${participationId}/pay-commission/verify`, { paymentIntentId }),
    // Factures (legacy FAC-2026 — serie close, consultables)
    getInvoices: () => apiFetch('/producer/invoices').then(normalizeParticipations),
    downloadInvoicePdf: async (invoiceId) => {
      const response = await fetch(`${API_BASE_URL}/invoices/${invoiceId}/pdf`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error(`Erreur ${response.status}`);
      return response.blob();
    },
    // Factures ModuleInvoice (COM marche + pieces Tournees) — v7.2.3 Ma facturation
    getModuleInvoices: () => apiFetch('/producer/module-invoices'),
    downloadModuleInvoicePdf: async (invoiceId) => {
      const response = await fetch(`${API_BASE_URL}/producer/module-invoices/${invoiceId}/pdf`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error(`Erreur ${response.status}`);
      return response.blob();
    },
    // Upload photos
    uploadLogo: async (file) => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${API_BASE_URL}/producer/upload/logo`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      const json = await response.json();
      if (!response.ok) throw new Error(json.message || `Erreur ${response.status}`);
      return json.data !== undefined ? json.data : json;
    },
    uploadOfferPhotos: async (offerId, files) => {
      const formData = new FormData();
      files.forEach((f) => formData.append('files', f));
      const response = await fetch(`${API_BASE_URL}/producer/upload/offer-photos/${offerId}`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      const json = await response.json();
      if (!response.ok) throw new Error(json.message || `Erreur ${response.status}`);
      return json.data !== undefined ? json.data : json;
    },
    uploadOrgPhotos: async (files) => {
      const formData = new FormData();
      files.forEach((f) => formData.append('files', f));
      const response = await fetch(`${API_BASE_URL}/producer/upload/photos`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      const json = await response.json();
      if (!response.ok) throw new Error(json.message || `Erreur ${response.status}`);
      return json.data !== undefined ? json.data : json;
    },
    // Stripe Connect (ADR-051) — paiement en ligne des tournees
    getStripeStatus: () => apiFetch('/producer/stripe/status'),
    createStripeOnboardingLink: () => apiPost('/producer/stripe/onboarding-link', {}),
    // Onboarding embarque : Account Session (client_secret + cle publiable)
    createStripeAccountSession: () => apiPost('/producer/stripe/account-session', {}),
  },

  // RINA — Assistante IA
  rina: {
    chat: (messages, context) => apiPost('/rina/chat', { messages, context }),
  },

  // Favoris producteurs
  favorites: {
    get: () => apiFetch('/favorites'),
    add: (organizationId) => apiPost(`/favorites/${organizationId}`, {}),
    remove: (organizationId) => apiDelete(`/favorites/${organizationId}`),
    sync: (organizationIds) => apiPost('/favorites/sync', { organizationIds }),
    clearAll: () => apiDelete('/favorites'),
  },

  // Favoris produits
  productFavorites: {
    get: () => apiFetch('/favorites/products'),
    add: (productOfferId) => apiPost(`/favorites/products/${productOfferId}`, {}),
    remove: (productOfferId) => apiDelete(`/favorites/products/${productOfferId}`),
    sync: (productOfferIds) => apiPost('/favorites/products/sync', { productOfferIds }),
    clearAll: () => apiDelete('/favorites/products'),
  },

  // Gestion PIN utilisateur
  users: {
    changePin: (currentPin, newPin) => apiPatch('/users/me/pin', { currentPin, newPin }),
    requestPinReset: (telephone) => apiPost('/users/pin-reset-request', { telephone }),
  },

  // Notifications push
  notifications: {
    getVapidKey: () => apiFetch('/notifications/vapid-key'),
    subscribe: (subscription) => apiPost('/notifications/subscribe', subscription),
    unsubscribe: async (endpoint) => {
      const response = await fetch(`${API_BASE_URL}/notifications/subscribe`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ endpoint }),
      });
      return response.json();
    },
  },
};
